import React, {useCallback, useContext, useEffect, useState} from 'react';
import {Button, Text, Modal, Dialogue, LoadingSpinner, Icon} from '@albert-io/atomic';
import {ButtonVariant} from '@albert-io/atomic/atoms/Button/Button.react';
import {CleverCourseSyncModelV1} from 'resources/GeneratedModels/CleverCourseSync/CleverCourseSyncModel.v1';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {invalidateClassroomsQueries} from 'client/Classrooms/Teacher/NewClassroomModal/newClassroomModal.utils';
import {callAction} from 'client/framework';
import sessionLifecycleActions from 'client/Session/SessionLifecycleActions';
import moment from 'moment';

import {CleverSyncResults, getUserQuery} from './util';
import {CleverSyncDispatch, CleverSyncContext, CleverSyncAction} from './CleverSyncProvider';
import SyncResults from './SyncResults';
import './clever-sync.scss';

const RETRY_INTERVAL = 2000; // 2000ms

const getRunningJobQuery = (id: string) =>
  resource('clever_course_sync_v1').mandarkEndpoint(['clever_course_syncs_v1', id]);

const createSyncJob = async (
  onStart: () => void,
  completeJob: (jobResult: CleverSyncResults) => void
) => {
  onStart();
  const sync = new CleverCourseSyncModelV1().addRelationship({
    type: 'user_v2',
    relation: masqueradeStore.getUserIdByMasqueradeState()
  });
  try {
    const result = await sync.save();
    const jobId = result.toJS().id;
    pollCleverCourseSync(jobId, completeJob);
  } catch (e: any) {
    completeJob({success: false, message: 'Unknown error'});
  }
};

const pollCleverCourseSync = (
  jobId: string,
  completeJob: (jobResult: CleverSyncResults) => void
) => {
  const interval = setInterval(async () => {
    const runningJob = await getRunningJobQuery(jobId).getResourcePromise();
    getRunningJobQuery(jobId).invalidateInterest();
    if (runningJob.getStatus() === 'finished') {
      clearInterval(interval);
      const results: CleverSyncResults = runningJob.getResults().toJS();
      completeJob(results);
    }
  }, RETRY_INTERVAL);
};

const onCloseModal = (dispatch: (value: CleverSyncAction) => void) => {
  getUserQuery().invalidateInterest();
  invalidateClassroomsQueries();
  dispatch({type: 'closeModal'});
};

const SyncIcon = () => (
  <Icon className='sync-icon u-mar-b_5 u-mar-t_1' icon='arrows-rotate' iconStyle='light' />
);

const ErrorIcon = () => (
  <Icon
    color='danger'
    className='sync-icon u-mar-b_5 u-mar-t_1'
    icon='triangle-exclamation'
    iconStyle='light'
  />
);

const WarningIcon = () => (
  <Icon
    color='warning'
    className='sync-icon u-mar-b_5 u-mar-t_1'
    icon='circle-exclamation-check'
    iconStyle='light'
  />
);

const ContactText = () => (
  <Text className='u-mar-b_2 u-pad-t_3'>
    Reach out to <strong>hello@albert.io</strong> if you need assistance.
  </Text>
);

const CloseModalButton = () => {
  const dispatch = useContext(CleverSyncDispatch);

  return (
    <Button
      variant='outlined'
      onClick={() => {
        onCloseModal(dispatch);
      }}
      className='u-mar-t_3 u-width_100pc'
    >
      Dismiss
    </Button>
  );
};

const InlineButton = () => {
  const state = useContext(CleverSyncContext);
  const dispatch = useContext(CleverSyncDispatch);

  const onComplete = useCallback(
    (syncResults: CleverSyncResults) => {
      dispatch({
        type: 'setLoading',
        value: false
      });
      dispatch({
        type: 'setSyncResults',
        syncResults
      });
      getUserQuery().invalidateInterest();
      invalidateClassroomsQueries();
      if (syncResults.success && syncResults.results.some((r) => r.success)) {
        dispatch({
          type: 'setSyncDate',
          date: moment()
        });
      }
      if (
        !syncResults.success ||
        !syncResults.results.every((r) => r.success) ||
        syncResults.results.some((r) => r.success && r.status === 'archived')
      ) {
        dispatch({
          type: 'openModal'
        });
      }
    },
    [dispatch]
  );
  const onStart = useCallback(() => {
    dispatch({
      type: 'setLoading',
      value: true
    });
  }, [dispatch]);

  useEffect(() => {
    if (state.currentlyInProgressSyncId) {
      onStart();
      pollCleverCourseSync(state.currentlyInProgressSyncId, onComplete);
      dispatch({type: 'setCurrentlyInProgressSyncId'});
    }
  }, [onComplete, onStart, state.currentlyInProgressSyncId, dispatch]);

  const buttonMessage = state.lastSyncDate ? 'Sync With Clever' : 'Import & Sync With Clever';

  return (
    <Button
      variant='outlined'
      className={`u-mar-r_1 ${state.loading && `u-pointer-events_none`}`}
      onClick={() => {
        if (state.userCleverId && state.userCleverDistrictId) {
          createSyncJob(onStart, onComplete);
        } else {
          dispatch({type: 'openLogoutModal'});
        }
      }}
    >
      {state.loading ? (
        <>
          <Icon spin icon='sync' className='u-mar-r_1' /> Syncing...
        </>
      ) : (
        buttonMessage
      )}
    </Button>
  );
};

const getSyncStatus = (
  syncResults?: CleverSyncResults
): 'all' | 'some' | 'none' | 'error' | 'empty' | null => {
  if (!syncResults) {
    return null;
  }
  if (syncResults.success === false) {
    return 'error';
  }
  if (syncResults.results.length === 0) {
    return 'empty';
  }
  if (syncResults.results.every((r) => r.success)) {
    return 'all';
  }
  if (syncResults.results.every((r) => !r.success)) {
    return 'none';
  }
  return 'some';
};

const CleverSyncModal = () => {
  const state = useContext(CleverSyncContext);
  const dispatch = useContext(CleverSyncDispatch);
  const onComplete = (syncResults: CleverSyncResults) => {
    dispatch({
      type: 'setSyncResults',
      syncResults
    });
    dispatch({
      type: 'setLoading',
      value: false
    });
    if (syncResults.success && syncResults.results.some((r) => r.success)) {
      dispatch({
        type: 'setSyncDate',
        date: moment()
      });
    }
  };
  const onStart = () => {
    dispatch({
      type: 'setLoading',
      value: true
    });
  };

  const syncStatus = getSyncStatus(state.syncResults);
  const specificError = !state.syncResults?.success && state.syncResults?.message;

  return (
    <Modal
      isLoading={false}
      closeModalPredicate={() => false}
      focusCloseButton={false}
      ariaLabel='clever sync modal'
      handleClose={() => {
        onCloseModal(dispatch);
      }}
    >
      {() => (
        <>
          {state.loading && (
            <Dialogue inModal className='clever-sync' hideCloseBtn size='s'>
              <Dialogue.Body align='center'>
                <LoadingSpinner size={3} className='u-color_slate-600 u-mar-b_5 u-mar-t_1' />
                <Text className='u-mar-t_5'>Sync in progress...</Text>
              </Dialogue.Body>
            </Dialogue>
          )}
          {syncStatus === 'all' && (
            <Dialogue inModal className='clever-sync' hideCloseBtn size='l'>
              <Dialogue.Body className='u-text-align_center u-display_flex u-flex-direction_column u-align-items_center'>
                <Icon color='success' className='sync-icon u-mar-b_5' icon='check' />
                <Text className='u-mar-b_5' bold>
                  Your Clever rosters and classrooms were all imported successfully!
                </Text>
                <SyncResults className='sync-results' syncResults={state.syncResults} />
                <CloseModalButton />
              </Dialogue.Body>
            </Dialogue>
          )}
          {syncStatus === 'some' && (
            <Dialogue inModal className='clever-sync' hideCloseBtn size='l'>
              <Dialogue.Body className='u-text-align_center u-display_flex u-flex-direction_column u-align-items_center'>
                <WarningIcon />
                <Text className='u-mar-b_5' bold>
                  Review changes from Clever:
                </Text>
                <SyncResults className='sync-results' syncResults={state.syncResults} />
                <ContactText />
                <CloseModalButton />
              </Dialogue.Body>
            </Dialogue>
          )}
          {syncStatus === 'none' && (
            <Dialogue inModal className='clever-sync' hideCloseBtn size='l'>
              <Dialogue.Body className='u-text-align_center u-display_flex u-flex-direction_column u-align-items_center'>
                <ErrorIcon />
                <Text className='u-mar-b_5' bold>
                  Import failed.
                </Text>
                <SyncResults className='sync-results' syncResults={state.syncResults} />
                <ContactText />
                <CloseModalButton />
              </Dialogue.Body>
            </Dialogue>
          )}
          {syncStatus === 'error' && specificError === 'User not found in Clever' && (
            <Dialogue inModal className='clever-sync' hideCloseBtn size='s'>
              <Dialogue.Body align='center'>
                <ErrorIcon />
                <Text className='u-mar-b_2' bold>
                  Incorrect Clever Sharing Rules
                </Text>
                <Text className='u-mar-b_3'>
                  Your account is not included on your district&apos;s Clever sharing rules for the
                  &quot;Albert (Rostering)&quot; app.
                </Text>
                <Text className='u-mar-b_2 u-pad-t_3'>
                  Please ask your district or school tech admin to add you to their sharing rules
                  for the &quot;Albert (Rostering)&quot; app.
                </Text>
                <CloseModalButton />
              </Dialogue.Body>
            </Dialogue>
          )}
          {syncStatus === 'error' && specificError !== 'User not found in Clever' && (
            <Dialogue inModal className='clever-sync' hideCloseBtn size='s'>
              <Dialogue.Body align='center'>
                <ErrorIcon />
                <Text className='u-mar-b_2' bold>
                  Something went wrong.
                </Text>
                <Text className='u-mar-b_3'>Your Clever classrooms were not imported.</Text>
                <ContactText />
                <CloseModalButton />
              </Dialogue.Body>
            </Dialogue>
          )}
          {syncStatus === 'empty' && (
            <Dialogue
              inModal
              className='clever-sync'
              title='Import & Sync With Clever'
              handleClose={() => {
                onCloseModal(dispatch);
              }}
            >
              <Dialogue.Body align='center'>
                <WarningIcon />
                <Text className='u-mar-b_3' bold>
                  No classes to import.
                </Text>
                <Text>
                  This may be because your district is not sharing your Clever section data.
                </Text>
                <ContactText />
                <CloseModalButton />
              </Dialogue.Body>
            </Dialogue>
          )}
          {!syncStatus && !state.loading && (
            <Dialogue inModal hideCloseBtn className='clever-sync' size='s'>
              <Dialogue.Body align='center'>
                <SyncIcon />
                <Text className='u-mar-b_5'>
                  Your entire roster and classrooms will be imported from Clever. Once imported,
                  you&#39;ll be able to sync any changes made.
                </Text>
                <div className='u-display_flex u-flex-direction_column u-gap_1'>
                  <Button
                    onClick={() => {
                      createSyncJob(onStart, onComplete);
                    }}
                  >
                    Import &amp; Sync With Clever
                  </Button>
                  <Button
                    variant='outlined'
                    onClick={() => {
                      onCloseModal(dispatch);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </Dialogue.Body>
            </Dialogue>
          )}
        </>
      )}
    </Modal>
  );
};

export const CleverSyncButtonWithModal = ({
  variant,
  className
}: {
  variant?: ButtonVariant;
  className?: string;
}) => {
  const dispatch = useContext(CleverSyncDispatch);
  const state = useContext(CleverSyncContext);

  return (
    <>
      <Button
        className={className}
        variant={variant}
        onClick={() => {
          if (state.userCleverId && state.userCleverDistrictId) {
            dispatch({type: 'openModal'});
          } else {
            dispatch({type: 'openLogoutModal'});
          }
        }}
      >
        Import &amp; Sync With Clever
      </Button>
    </>
  );
};

const CleverNotLoggedInModal = () => {
  const dispatch = useContext(CleverSyncDispatch);

  const [loggingOut, setLoggingOut] = useState(false);

  return (
    <Modal
      isLoading={false}
      closeModalPredicate={() => false}
      focusCloseButton={false}
      ariaLabel='clever not logged in modal'
      handleClose={() => {
        dispatch({type: 'closeLogoutModal'});
      }}
    >
      {() => (
        <Dialogue inModal size='s' hideCloseBtn>
          <Dialogue.Body align='center'>
            <Text>
              Before you can import and sync, you&apos;ll need to log out of Albert and log back in
              with Clever.
            </Text>
            <div className='u-display_flex u-flex-direction_column u-gap_1 u-mar-t_5'>
              <Button
                className={loggingOut ? 'u-pointer-events_none' : ''}
                onClick={() => {
                  setLoggingOut(true);
                  callAction(sessionLifecycleActions.END_SESSION);
                }}
              >
                {loggingOut ? 'Logging out...' : 'Log Out Of Albert'}
              </Button>
              <Button
                variant='outlined'
                onClick={() => {
                  dispatch({type: 'closeLogoutModal'});
                }}
              >
                Cancel
              </Button>
            </div>
          </Dialogue.Body>
        </Dialogue>
      )}
    </Modal>
  );
};

export const CleverSyncButton = ({variant}: {variant?: ButtonVariant}) => {
  const state = useContext(CleverSyncContext);

  let button: any = null;
  if (!state.lastSyncDate) {
    button = <CleverSyncButtonWithModal className='u-mar-r_1' variant={variant} />;
  } else {
    button = (
      <>
        {state.lastSyncDate && !state.loading && (
          <Text emphasis='medium' size='s' className='u-mar-r_2'>
            Last successful sync {state.lastSyncDate.format('M/D/YYYY')} at{' '}
            {state.lastSyncDate.format('LT')}
          </Text>
        )}
        <InlineButton />
      </>
    );
  }
  return (
    <>
      {button}
      {state.isModalOpen && <CleverSyncModal />}
      {state.isLogoutModalOpen && <CleverNotLoggedInModal />}
    </>
  );
};
